import {
  QUEUE_NAME_RULES,
  QUEUE_PEEK_MAX,
  QUEUE_RECEIVE_VISIBILITY_MIN_SECONDS,
  QUEUE_TTL_DEFAULT_SECONDS,
  QUEUE_TTL_FORM_MAX_SECONDS,
  QUEUE_TTL_INFINITE,
  QUEUE_TTL_MIN_SECONDS,
  QUEUE_VISIBILITY_MAX_SECONDS,
} from '../../types/azureStorage';

type ReceiveValidation = { ok: true } | { ok: false; field: 'count' | 'visibilityTimeoutSeconds'; reason: string };
type SendValidation = { ok: true } | { ok: false; field: 'ttlSeconds' | 'visibilityTimeoutSeconds'; reason: string };
type ValueValidation = { ok: true } | { ok: false; reason: string };

const integerInRange = (value: unknown, min: number, max: number): value is number =>
  typeof value === 'number' && Number.isInteger(value) && value >= min && value <= max;

export function validateReceiveRequest(value: { count: unknown; visibilityTimeoutSeconds: unknown }): ReceiveValidation {
  if (!integerInRange(value.count, 1, QUEUE_PEEK_MAX)) {
    return { ok: false, field: 'count', reason: `Count must be an integer between 1 and ${QUEUE_PEEK_MAX}.` };
  }
  if (!integerInRange(value.visibilityTimeoutSeconds, QUEUE_RECEIVE_VISIBILITY_MIN_SECONDS, QUEUE_VISIBILITY_MAX_SECONDS)) {
    return { ok: false, field: 'visibilityTimeoutSeconds', reason: `Visibility must be an integer between 1 and ${QUEUE_VISIBILITY_MAX_SECONDS} seconds.` };
  }
  return { ok: true };
}

export function validateSendOptions(value: { ttlSeconds?: unknown; visibilityTimeoutSeconds?: unknown }): SendValidation {
  const ttl = value.ttlSeconds;
  if (ttl !== undefined && ttl !== QUEUE_TTL_INFINITE && !integerInRange(ttl, QUEUE_TTL_MIN_SECONDS, QUEUE_TTL_FORM_MAX_SECONDS)) {
    return { ok: false, field: 'ttlSeconds', reason: `TTL must be -1 or an integer between 1 and ${QUEUE_TTL_FORM_MAX_SECONDS} seconds.` };
  }
  const visibility = value.visibilityTimeoutSeconds;
  if (visibility !== undefined && !integerInRange(visibility, 0, QUEUE_VISIBILITY_MAX_SECONDS)) {
    return { ok: false, field: 'visibilityTimeoutSeconds', reason: `Initial visibility must be an integer between 0 and ${QUEUE_VISIBILITY_MAX_SECONDS} seconds.` };
  }
  const effectiveTtl = ttl ?? QUEUE_TTL_DEFAULT_SECONDS;
  if (effectiveTtl !== QUEUE_TTL_INFINITE && typeof visibility === 'number' && visibility >= effectiveTtl) {
    return { ok: false, field: 'visibilityTimeoutSeconds', reason: 'Initial visibility must be shorter than a finite message TTL.' };
  }
  return { ok: true };
}

export function validateUpdateVisibility(value: unknown, bounds: { remainingLifetimeSeconds?: number } = {}): ValueValidation {
  if (!integerInRange(value, 0, QUEUE_VISIBILITY_MAX_SECONDS)) {
    return { ok: false, reason: `Visibility must be an integer between 0 and ${QUEUE_VISIBILITY_MAX_SECONDS} seconds.` };
  }
  const remaining = bounds.remainingLifetimeSeconds;
  if (remaining !== undefined && (remaining <= 0 || value > remaining)) {
    return { ok: false, reason: `A message's visibility cannot be set past its expiry — this one expires in ${Math.max(0, remaining)}\u00a0s.` }; // U35: the unit never wraps away from its number
  }
  return { ok: true };
}

export function validateQueueName(name: unknown): ValueValidation {
  if (typeof name !== 'string' || name.length < QUEUE_NAME_RULES.min || name.length > QUEUE_NAME_RULES.max || !QUEUE_NAME_RULES.pattern.test(name)) {
    return { ok: false, reason: 'Queue names must be 3–63 lowercase letters or digits, with single hyphens between segments.' };
  }
  return { ok: true };
}
